import type { CanvasCollectionId } from "./canvasArtworkCatalog";
import {
  canvasImageUrls,
  primaryCanvasImageUrl,
} from "../lib/canvasArtworkHelpers";

/** Physical size of the stretched canvas in centimeters. */
export type CanvasDimensions = {
  widthCm: number;
  heightCm: number;
  depthCm: number;
};

export type CanvasArtworkImageRole =
  | "front"
  | "detail"
  | "edge"
  | "studio"
  | "installation";

export type CanvasArtworkImage = {
  id: string;
  /** Path under studio CDN, e.g. `canvas-arts/lion_animals/lion_anmials.webp` */
  file: string;
  role?: CanvasArtworkImageRole;
};

export type CanvasArtwork = {
  id: string;
  images: CanvasArtworkImage[];
  collection: CanvasCollectionId;
  dimensions: CanvasDimensions;
  /** Collector price in USD — originals are positioned at $10k+. */
  investmentPriceUsd: number;
  available?: boolean;
  featured?: boolean;
  /** Resolved primary image URL (CDN). */
  imageUrl: string;
  /** All resolved image URLs in display order. */
  imageUrls: string[];
};

type RawCanvasArtwork = Omit<CanvasArtwork, "imageUrl" | "imageUrls">;

function canvasImg(
  rel: string,
  role: CanvasArtworkImageRole,
  id?: string,
): CanvasArtworkImage {
  const stem = rel.replace(/\.webp$/, "").split("/").pop() ?? rel;
  return {
    id: id ?? stem,
    file: `canvas-arts/${rel}`,
    role,
  };
}

const rawCanvasArtworks: RawCanvasArtwork[] = [
  {
    id: "hope-between-garbage-cans",
    images: [
      canvasImg("hope_between_grabadge_cans/hope_between_grabadge_cans.webp", "front"),
      canvasImg("hope_between_grabadge_cans/dsc_0033.webp", "studio"),
      canvasImg("hope_between_grabadge_cans/dsc_0034.webp", "studio"),
      canvasImg("hope_between_grabadge_cans/dsc_0035.webp", "studio"),
      canvasImg("hope_between_grabadge_cans/dsc_0036.webp", "studio"),
      canvasImg("hope_between_grabadge_cans/dsc_0037.webp", "studio"),
      canvasImg("hope_between_grabadge_cans/dsc_0038.webp", "studio"),
    ],
    collection: "realistic",
    dimensions: { widthCm: 90, heightCm: 70, depthCm: 3 },
    investmentPriceUsd: 7_200,
    available: true,
  },
  {
    id: "lion",
    images: [
      canvasImg("lion_animals/lion_anmials.webp", "front"),
      canvasImg("lion_animals/lion_close_up1.webp", "detail"),
      canvasImg("lion_animals/lion_close_up2.webp", "detail"),
    ],
    collection: "animals",
    dimensions: { widthCm: 50, heightCm: 80, depthCm: 3 },
    investmentPriceUsd: 6_500,
    available: true,
    featured: true,
  },
  {
    id: "conversation-on-mars",
    images: [
      canvasImg("conversation_on_mars/conversation_on_mars.webp", "front"),
      canvasImg("conversation_on_mars/dsc_0055_1.webp", "studio"),
      canvasImg("conversation_on_mars/dsc_0058.webp", "studio"),
    ],
    collection: "figurative",
    dimensions: { widthCm: 50, heightCm: 70, depthCm: 4 },
    investmentPriceUsd: 11_500,
    available: true,
    featured: true,
  },
  {
    id: "elephant",
    images: [
      canvasImg("elephant_1_anmials.webp", "front"),
    ],
    collection: "animals",
    dimensions: { widthCm: 80, heightCm: 60, depthCm: 3.8 },
    investmentPriceUsd: 8_200,
    available: true,
  },
  {
    id: "mushrooms",
    images: [
      canvasImg("mushrooms_better.webp", "front"),
    ],
    collection: "realistic",
    dimensions: { widthCm: 90, heightCm: 70, depthCm: 4 },
    investmentPriceUsd: 7_200,
    available: true,
  },
  {
    id: "seal",
    images: [
      canvasImg("seal_anmials/seal_anmials.webp", "front"),
      canvasImg("seal_anmials/close_up.webp", "detail"),
      canvasImg("seal_anmials/close_up2.webp", "detail"),
    ],
    collection: "animals",
    dimensions: { widthCm: 30, heightCm: 30, depthCm: 1.6 },
    investmentPriceUsd: 3_800,
    available: true,
  },
  {
    id: "hippo",
    images: [
      canvasImg("hipo_animals/hipo_animals.webp", "front"),
      canvasImg("hipo_animals/hipo_in_children_room.webp", "installation"),
    ],
    collection: "animals",
    dimensions: { widthCm: 80, heightCm: 60, depthCm: 3.8 },
    investmentPriceUsd: 4_200,
    available: false,
  },
  {
    id: "abstract-mountains-trails",
    images: [
      canvasImg("abstract_mountains_trails.webp", "front"),
    ],
    collection: "figurative",
    dimensions: { widthCm: 100, heightCm: 80, depthCm: 4 },
    investmentPriceUsd: 9_800,
    available: true,
  },
  {
    id: "tahdig",
    images: [
      canvasImg("tahadig_food.webp", "front"),
    ],
    collection: "food",
    dimensions: { widthCm: 90, heightCm: 70, depthCm: 4 },
    investmentPriceUsd: 6_200,
    available: false,
    featured: true,
  },
  {
    id: "jerusalem-artichoke",
    images: [
      canvasImg("jerusalem_artichock_food.webp", "front"),
    ],
    collection: "food",
    dimensions: { widthCm: 70, heightCm: 50, depthCm: 3.8 },
    investmentPriceUsd: 6_800,
    available: false,
  },
  {
    id: "matti-caspi-tribute",
    images: [
      canvasImg("matti_caspie_tribute_lo_tov_heiot_adam_levado/matti_caspie_tribute_lo_tov_heiot_adam_levado.webp", "front"),
      canvasImg("matti_caspie_tribute_lo_tov_heiot_adam_levado/close_up1233442.webp", "detail"),
      canvasImg("matti_caspie_tribute_lo_tov_heiot_adam_levado/close_up2332312312341241.webp", "detail"),
    ],
    collection: "memorial",
    dimensions: { widthCm: 50, heightCm: 70, depthCm: 3 },
    investmentPriceUsd: 5_200,
    available: true,
  },
  {
    id: "giraffe",
    images: [
      canvasImg("girraffe_anmials/girraffe_anmials.webp", "front"),
      canvasImg("girraffe_anmials/girraffe_in_room.webp", "installation"),
    ],
    collection: "animals",
    dimensions: { widthCm: 80, heightCm: 60, depthCm: 3.8 },
    investmentPriceUsd: 8_500,
    available: false,
  },
  {
    id: "sissi-with-a-pearl-earring",
    images: [
      canvasImg("sissi_with_a_pearl_earing_animals/sissi_with_a_pearl_earing_animals.webp", "front"),
      canvasImg("sissi_with_a_pearl_earing_animals/111close_up.webp", "detail"),
      canvasImg("sissi_with_a_pearl_earing_animals/111close_up2.webp", "detail"),
      canvasImg("sissi_with_a_pearl_earing_animals/close_up123.webp", "detail"),
      canvasImg("sissi_with_a_pearl_earing_animals/close_up_nose221.webp", "detail"),
      canvasImg("sissi_with_a_pearl_earing_animals/sissi_wity_pearl_earring_in_room.webp", "installation"),
    ],
    collection: "animals",
    dimensions: { widthCm: 60, heightCm: 90, depthCm: 3 },
    investmentPriceUsd: 9_500,
    available: true,
    featured: true,
  },
  {
    id: "dan-schachter-tribute",
    images: [
      canvasImg("dan_schacter_tribute.webp", "front"),
    ],
    collection: "music",
    dimensions: { widthCm: 100, heightCm: 80, depthCm: 4 },
    investmentPriceUsd: 13_000,
    available: false,
    featured: true,
  },
  {
    id: "nakhlieli",
    images: [
      canvasImg("nakhlieli_animals/nakhlieli_animals.webp", "front"),
      canvasImg("nakhlieli_animals/nakhlieli_close_up.webp", "detail"),
      canvasImg("nakhlieli_animals/nakhlieli_close_up2.webp", "detail"),
    ],
    collection: "animals",
    dimensions: { widthCm: 40, heightCm: 34, depthCm: 1.6 },
    investmentPriceUsd: 3_400,
    available: true,
    featured: true,
  },
  {
    id: "tikva-shuk",
    images: [
      canvasImg("tikva_shuk/tikva_shuk.webp", "front"),
      canvasImg("tikva_shuk/dsc_0039.webp", "studio"),
      canvasImg("tikva_shuk/dsc_0040.webp", "studio"),
      canvasImg("tikva_shuk/dsc_0041.webp", "studio"),
      canvasImg("tikva_shuk/dsc_0043.webp", "studio"),
      canvasImg("tikva_shuk/dsc_0044.webp", "studio"),
      canvasImg("tikva_shuk/dsc_0053.webp", "studio"),
      canvasImg("tikva_shuk/dsc_0054.webp", "studio"),
      canvasImg("tikva_shuk/dsc_0063.webp", "studio"),
    ],
    collection: "figurative",
    dimensions: { widthCm: 60, heightCm: 50, depthCm: 3 },
    investmentPriceUsd: 10_500,
    available: true,
    featured: true,
  },
];

export const canvasArtworks: CanvasArtwork[] = rawCanvasArtworks.map((work) => ({
  ...work,
  imageUrl: primaryCanvasImageUrl(work),
  imageUrls: canvasImageUrls(work),
}));
